import {
  useAssets,
  useAssetsIcons,
  useDepositWithdrawAssetsDictionary,
} from '@/shared/api';
import { useMemo } from 'react';
import type { AssetOption, NetworkOption } from './types';

type Props = {
  selectedAssetCode: string;
};

type CreateCryptoAddressFormData = {
  isLoading: boolean;
  isError: boolean;
  options: {
    assets: AssetOption[];
    networks: NetworkOption[];
  };
};

export const useCreateCryptoAddressFormData = ({
  selectedAssetCode,
}: Props): CreateCryptoAddressFormData => {
  const { assetsIconsData, isAssetsIconsLoading, assetsIconsError } =
    useAssetsIcons();

  const {
    depositWithdrawAssetsData,
    depositWithdrawAssetsLoading,
    depositWithdrawAssetsError,
  } = useDepositWithdrawAssetsDictionary();

  const {
    data: assetsData,
    error: assetsError,
    isLoading: isAssetsLoading,
  } = useAssets({
    enabled: !!depositWithdrawAssetsData,
  });

  const isError =
    !!depositWithdrawAssetsError || !!assetsError || !!assetsIconsError;

  const isReady =
    !!depositWithdrawAssetsData &&
    !!assetsData &&
    !!assetsIconsData &&
    !depositWithdrawAssetsLoading &&
    !isAssetsLoading &&
    !isAssetsIconsLoading;

  const isLoading = !isReady && !isError;

  const { assets, networksByAsset } = useMemo(() => {
    if (!isReady) {
      return {
        assets: [],
        networksByAsset: {},
      };
    }

    const assetsMap = new Map<string, AssetOption>();
    const networksMap: Record<string, NetworkOption[]> = {};

    const networks = depositWithdrawAssetsData.value.withdraw.crypto ?? [];

    for (const network of networks) {
      for (const asset of network.assets ?? []) {
        if (!assetsMap.has(asset.code)) {
          const assetInfo = assetsData.value.find(a => a.code === asset.code);

          assetsMap.set(asset.code, {
            value: asset.code,
            code: asset.code,
            name: assetInfo?.name ?? asset.code,
            icon: assetsIconsData.value?.[asset.code] ?? '',
          });
        }

        networksMap[asset.code] ??= [];

        if (
          !networksMap[asset.code].some(n => n.code === network.networkCode)
        ) {
          networksMap[asset.code].push({
            value: network.networkName,
            code: network.networkCode,
            name: network.networkName,
          });
        }
      }
    }

    return {
      assets: Array.from(assetsMap.values()),
      networksByAsset: networksMap,
    };
  }, [isReady, depositWithdrawAssetsData, assetsData, assetsIconsData]);

  const networks = useMemo(() => {
    if (!isReady || !selectedAssetCode) {
      return [];
    }

    return networksByAsset[selectedAssetCode] ?? [];
  }, [isReady, selectedAssetCode, networksByAsset]);

  return {
    isLoading,
    isError,
    options: {
      assets,
      networks,
    },
  };
};
